/**
 * Domain-scoped seeded RNG. The foundation for Phase 11 determinism and the
 * v0.2 replay format.
 *
 * Architecture (HANDOFF.md pattern #5):
 *
 *   masterSeed → seedrandom("master:domain", { state: true }) per domain
 *
 * Each domain's stream is independent: an extra encounter roll never shifts
 * the battle PRNG sequence, and vice versa. The ARC4 variant of seedrandom
 * is the only one whose internal state survives JSON.stringify (validated in
 * spike 0b-c); the { state: true } flag turns it on.
 *
 * Master seed for v0.1 is generated per-run from crypto.getRandomValues and
 * persisted through saves. v0.2 will replace this with the weekly-rotating
 * tournament seed. The domain enumeration is forward-compatible — adding a
 * new domain in v0.2 means adding it to DOMAIN_NAMES + a v3→v4 migration
 * that initializes its state.
 */
import seedrandom from 'seedrandom';

/** seedrandom's PRNG instance with the `state: true` option enabled. The
 *  upstream @types/seedrandom types `state` as a method that requires a
 *  type assertion at the use site; aliasing here keeps that local. */
type StatefulPRNG = seedrandom.PRNG & { state: () => unknown };

/** Helper: create a stateful ARC4 stream. The 2nd-arg `state: true` flag
 *  is what enables ARC4 (the default seedrandom variant doesn't expose a
 *  serializable state) — validated in spike 0b-c. */
function makeStreamFromKey(key: string): StatefulPRNG {
  return seedrandom(key, { state: true }) as StatefulPRNG;
}

/** Helper: resume an ARC4 stream from a previously-captured state. The
 *  empty-string seed is intentional — when `state` is an object, seedrandom
 *  uses it directly and ignores the seed. */
function makeStreamFromState(state: unknown): StatefulPRNG {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return seedrandom('', { state: state as any }) as StatefulPRNG;
}

/** All RNG domains the game uses or will use. Stored in the save snapshot;
 *  adding a domain is a save-schema migration. v0.1 only consumes `encounter`
 *  + `battle`; the others are stubbed so future wiring is a no-migration
 *  change. */
export const DOMAIN_NAMES = [
  'encounter',
  'battle',
  'draft',
  'ivs',
  'trainer',
  'reward',
] as const;
export type DomainName = (typeof DOMAIN_NAMES)[number];

/** ARC4 internal state. seedrandom returns `unknown` so we keep that opaque. */
type ArcState = unknown;

export interface SeedSnapshot {
  /** Master seed string. Hex-encoded 128-bit value for v0.1; v0.2 replaces
   *  with a human-readable weekly key like '2026-W19-pallet'. */
  master: string;
  /** ARC4 state per domain. Restoring with seedrandom('', { state: <s> })
   *  resumes the stream byte-identically from where state() was captured. */
  domains: Record<DomainName, ArcState>;
}

/** Generate a fresh 128-bit master seed using the platform CSPRNG.
 *  Available on browsers and on Node ≥ 19 (vitest's default env). */
function freshMasterSeed(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

class SeedStore {
  private master: string = '';
  private rngs: Record<DomainName, StatefulPRNG> = {} as Record<
    DomainName,
    StatefulPRNG
  >;

  /** Initialize a fresh run. Generates a new master seed; each domain gets
   *  its own ARC4 stream keyed by `master:domain`. Idempotent — calling
   *  twice resets to a fresh state, which is what reset() relies on. */
  initFresh(): void {
    this.master = freshMasterSeed();
    for (const name of DOMAIN_NAMES) {
      this.rngs[name] = makeStreamFromKey(`${this.master}:${name}`);
    }
  }

  /** Initialize from a known master seed string. Used by tests + by v0.2
   *  weekly-seed plumbing. Each domain stream is reset to its starting
   *  state, derived deterministically from `master:domain`. */
  initFromMaster(master: string): void {
    this.master = master;
    for (const name of DOMAIN_NAMES) {
      this.rngs[name] = makeStreamFromKey(`${master}:${name}`);
    }
  }

  /** Restore from a save snapshot. Each domain's stream resumes from its
   *  captured internal state — subsequent rolls match what would have come
   *  next at save time. */
  restore(snapshot: SeedSnapshot): void {
    this.master = snapshot.master;
    for (const name of DOMAIN_NAMES) {
      const state = snapshot.domains[name];
      if (state === undefined) {
        // Domain added after this save was written — initialize it from
        // master:domain like a fresh run. Keeps old saves loadable.
        this.rngs[name] = makeStreamFromKey(`${this.master}:${name}`);
      } else {
        this.rngs[name] = makeStreamFromState(state);
      }
    }
  }

  serialize(): SeedSnapshot {
    const domains = {} as Record<DomainName, ArcState>;
    for (const name of DOMAIN_NAMES) {
      domains[name] = this.rngs[name]!.state();
    }
    return { master: this.master, domains };
  }

  /** [0, 1) — the standard rng signature. Use this for weighted picks,
   *  thresholds, etc. Throws if initFresh/initFromMaster/restore wasn't
   *  called yet (programmer error). */
  random(domain: DomainName): number {
    const rng = this.rngs[domain];
    if (!rng) throw new Error(`seed: domain '${domain}' not initialized`);
    return rng();
  }

  /** A 32-bit unsigned integer. Useful for deriving sim PRNGSeed tuples. */
  uint32(domain: DomainName): number {
    return Math.floor(this.random(domain) * 0x100000000) >>> 0;
  }

  /** A bound rng function for callers that take `() => number` (e.g.
   *  pickEncounter). Returned function reads the live domain stream every
   *  call — no snapshot. */
  rngFor(domain: DomainName): () => number {
    return () => this.random(domain);
  }

  getMaster(): string {
    return this.master;
  }

  /** Derived, NON-persisted stream for roam-mode spawn population.
   *
   *  Keyed off (master, region, visit) instead of living in
   *  DOMAIN_NAMES: the population must be RE-derivable after a reload
   *  (roamer positions are derived-not-persisted, per the follower's
   *  rule), and a live serialized stream would hand back DIFFERENT
   *  numbers on the post-reload re-derivation — reload-to-reroll
   *  scumming for free. A pure key means the same visit always yields
   *  the same population; repopulation happens only when runState
   *  bumps the region's visit counter on genuine re-entry.
   *
   *  Deliberately NOT in DOMAIN_NAMES / SeedSnapshot — nothing to
   *  serialize, zero surface on the encounter-determinism CI gate. */
  spawnRngFor(region: string, visit: number): () => number {
    const rng = makeStreamFromKey(`${this.master}:spawn:${region}:${visit}`);
    return () => rng();
  }

  /** Reset to an uninitialized state. Used by Reset Run + tests. After
   *  reset(), call initFresh() before the next roll. */
  reset(): void {
    this.master = '';
    this.rngs = {} as Record<DomainName, StatefulPRNG>;
  }
}

export const seedStore = new SeedStore();
